import { authExchange } from "@urql/exchange-auth";
import { type CacheExchangeOpts, cacheExchange } from "@urql/exchange-graphcache";
import { createClient, errorExchange, fetchExchange } from "urql";
import schema from "../client/introspection.json";
import { clearTokens, getTokens } from "./authStore";
import { UNAUTHORIZED_QUERY } from "./constants";
import { withAuditInvalidation } from "./auditInvalidation";
import { updateFunctions } from "./updateList";

const mutationNames =
  schema.__schema.types.find((t) => t.name === "Mutation")?.fields?.map((f) => f.name) ?? [];

export const urqlClient = createClient({
  url: import.meta.env.VITE_APP_BACKEND_URI + import.meta.env.VITE_APP_BACKEND_GRAPHQL_PATH || "",
  suspense: true,
  requestPolicy: "cache-and-network",
  exchanges: [
    cacheExchange({
      // schema awareness lets graphcache reason about lists/nullability
      // https://commerce.nearform.com/open-source/urql/docs/graphcache/schema-awareness/
      schema: schema as CacheExchangeOpts["schema"],
      keys: new Proxy(
        {},
        {
          get(_target, typename) {
            // AuthClient has no uuid — it is keyed by clientId in our schema
            if (typename === "AuthClient") {
              return (data: { clientId?: string }) => data.clientId ?? null;
            }
            // key every other entity on its `uuid` (our schema uses uuid, not id);
            // types without a uuid resolve to null and are treated as embedded
            return (data: { uuid?: string }) => data.uuid ?? null;
          },
        },
      ),
      updates: {
        // generated per-mutation resolvers keep list queries in sync on
        // create/delete without any per-call-site `additionalTypenames`
        Mutation: withAuditInvalidation(mutationNames, updateFunctions),
      },
    }),
    authExchange(async (utils) => {
      return {
        addAuthToOperation(operation) {
          const tokens = getTokens();

          if (tokens) {
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${tokens.access_token}`,
            });
          }
          return operation;
        },
        willAuthError(_operation) {
          return !getTokens();
        },
        didAuthError(error, _operation) {
          return error.graphQLErrors.some((e) => e.extensions?.code === UNAUTHORIZED_QUERY);
        },
        async refreshAuth() {
          const tokens = getTokens();

          if (!tokens?.access_token || (tokens?.expires_at && tokens?.expires_at <= Date.now())) {
            localStorage.clear();
          }
        },
      };
    }),
    errorExchange({
      onError: (error) => {
        const isAuthError =
          error.graphQLErrors.some((e) => e.extensions?.code === UNAUTHORIZED_QUERY) ||
          error.networkError?.message === "Unauthorized";
        if (isAuthError) {
          clearTokens();
          window.location.href = "/";
        }
      },
    }),
    fetchExchange,
  ],
});
